import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { SubscriptionState } from 'src/app/_shared/state/subscription.state';
import { UserState } from 'src/app/_shared/state/user.state';
import { AppState } from 'src/app/app.state';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SubscriptionPlanService {
  $isPlanActive = new BehaviorSubject<boolean>(false);
  isPlanActive = this.$isPlanActive.asObservable();

  constructor(
    private http: HttpClient,
    private subscriptionState: SubscriptionState,
    private userState: UserState,
    private appState: AppState
  ) { }

  getSubscriptionPlan() {
    const roles = this.userState?.userDetails?.roles;
    const allowedRoles = ['agent', 'loanOfficer', 'investor'];

    if (!roles?.some((role) => allowedRoles.includes(role))) {
      return;
    }

    this.http
      .get(environment.prospektrBackendBaseURL + 'getSubscriptionPlan')
      .subscribe((res: any) => {
        if (res?.status === 'success' && res.data) {
          const planData = res.data;
          const user = { ...this.userState.userDetails };

          this.subscriptionState.planUsageValue = planData;

          const isTrial = !!planData?.isTrial && !planData?.trialExpired;
          const isLandingPlan = !!planData?.isLanding;
          const isExpiredTrialOnly =
            !!planData?.isTrial &&
            !isLandingPlan &&
            (planData?.trialExpired ||
              (planData?.daysRemaining !== undefined && Number(planData.daysRemaining) <= 0));

          user.renewalDate = planData?.renewalDate || null;
          user.isTrial = isTrial;
          user.isLandingPlan = isLandingPlan;

          // Expired trial without fallback plan should be inactive.
          // Trial users moved to landing/starter should remain active.
          if (isExpiredTrialOnly) {
            user.isPlanActive = false;
            user.userRole = 'trial';
            this.$isPlanActive.next(false);
          } else {
            user.isPlanActive = true;
            user.userRole = 'client';
            this.$isPlanActive.next(true);
          }

          this.userState.userDetails = user;
        }
      });
  }

  checkFeatureValidity(featureName?: string, showPopUp: boolean = true): boolean {
    const planUsage = this.subscriptionState.planUsageValue;
    const userDetails = this.userState.userDetails;
    const companyDetails = this.appState.companyInfoValue;

    if (companyDetails?.appConfig && !companyDetails.appConfig?.planSubscription?.isEnabled) {
      return true;
    }

    const roles = userDetails?.roles || [];
    if (!roles.includes('agent') && !roles.includes('loanOfficer') && !roles.includes('investor')) {
      return true;
    }

    const isInvestor = roles.includes('investor');
    const owner = planUsage?.planOwner || {};
    const ownerName = owner?.name?.trim() || 'your agent';

    const investorOwnerLabel =
      owner?.type === 'default_agent' ? ownerName : ownerName;

    // Block only real expired trial access, not landing/starter fallback
    if (planUsage?.isTrial && planUsage?.planType?.toLowerCase() === 'trial') {
      const remainingDays =
        planUsage?.daysRemaining !== undefined
          ? Number(planUsage.daysRemaining)
          : planUsage?.renewalDate
            ? Math.round(moment(planUsage.renewalDate).diff(moment(), 'days', true))
            : 0;

      if (remainingDays <= 0 || planUsage?.trialExpired) {
        if (showPopUp) {
          this.subscriptionState.planUpgradeMessage = {
            message: isInvestor
              ? `The trial period for connected agent ${investorOwnerLabel}'s subscription has ended. Please ask ${investorOwnerLabel} to upgrade the subscription to continue using this feature.`
              : 'Your trial period has ended. Please upgrade to continue using this feature.',
            showButton: isInvestor ? false : true
          };
        }
        return false;
      }
    }

    if (!featureName) {
      if (showPopUp) {
        this.subscriptionState.planUpgradeMessage = {
          message: isInvestor
            ? `Feature information is missing. Please contact support or connected agent ${investorOwnerLabel}.`
            : 'Feature name is missing. Please contact support.',
          showButton: isInvestor ? false : true
        };
      }
      return false;
    }

    const feature = planUsage?.featureList?.[featureName];

    if (!feature || !feature.enabled) {
      if (showPopUp) {
        this.subscriptionState.planUpgradeMessage = {
          message: isInvestor
            ? `This feature is not available under connected agent ${investorOwnerLabel}'s current subscription.\n\nPlease ask connected agent ${investorOwnerLabel} to update the subscription.`
            : 'This feature is not available in your current subscription.\n\nPlease update your subscription.',
          showButton: isInvestor ? false : true
        };
      }
      return false;
    }

    const hasUsageLimit =
      feature?.usageLimit !== undefined &&
      feature?.usageLimit !== null;

    if (hasUsageLimit) {
      const consumableRemaining = planUsage?.consumableRemaining?.[featureName];

      if (consumableRemaining !== undefined && consumableRemaining <= 0) {
        if (showPopUp) {
          this.subscriptionState.planUpgradeMessage = {
            message: isInvestor
              ? `The usage limit for ${formatFeatureName(featureName)} under connected agent ${investorOwnerLabel}'s subscription has been reached.\n\nPlease ask ${investorOwnerLabel} to upgrade the subscription or increase the available limits.`
              : `You have reached the limit for ${formatFeatureName(featureName)} in your current plan.\n\nPlease upgrade to increase your usage limits.`,
            showButton: isInvestor ? false : true
          };
        }
        return false;
      }
    }

    return true;
  }

}

function formatFeatureName(featureName: string): string {
  return featureName.replace(/([A-Z])/g, ' $1').toLowerCase();
}
